import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { BarChart3, Lock, ArrowRight } from "lucide-react";

const Analytics = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/analytics").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Failed"));
  }, []);

  if (user?.plan !== "pro") {
    return (
      <div className="min-h-screen flex items-center justify-center p-10">
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto mb-6 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px]">
            <Lock className="text-rose-400" size={22}/>
          </div>
          <div className="label-mono mb-3">/ locked · pro only</div>
          <h1 className="font-display text-3xl tightest font-semibold mb-4 leading-tight">Advanced analytics</h1>
          <p className="text-neutral-500 text-[0.9rem] mb-8 leading-relaxed">
            Pro unlocks detailed mastery signals across every subject you record — see where you&apos;re compounding and where you&apos;re leaking time.
          </p>
          <Link to="/pricing"><button className="btn-primary" data-testid="analytics-upgrade">Upgrade to Pro <ArrowRight size={13}/></button></Link>
        </div>
      </div>
    );
  }

  if (err) return <div className="p-10 text-neutral-400 text-[0.86rem]">{err}</div>;
  if (!data) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  const subjects = Object.entries(data.by_subject);
  const max = Math.max(1, ...subjects.map(([, v]) => v.recordings));

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <header className="mb-10">
        <div className="label-mono mb-3">/ analytics</div>
        <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">Your study pulse.</h1>
        <p className="text-neutral-500 mt-3 text-[0.92rem]">Aggregate signals across every subject you&apos;ve recorded.</p>
      </header>

      <div className="grid md:grid-cols-3 gap-3 mb-10">
        <Stat label="Recordings" value={data.total_recordings} />
        <Stat label="Generated items" value={data.total_items} />
        <Stat label="Minutes studied" value={data.total_minutes} />
      </div>

      <div className="glass p-8">
        <div className="label-mono mb-6 flex items-center gap-2"><BarChart3 size={12}/> by subject</div>
        <div className="space-y-4">
          {subjects.length === 0 && <div className="text-neutral-500 text-[0.86rem]">No data yet.</div>}
          {subjects.map(([s, v]) => (
            <div key={s} data-testid={`stat-${s}`}>
              <div className="flex justify-between text-[0.86rem] mb-2">
                <span className="text-white">{s}</span>
                <span className="text-neutral-500 tabular">
                  {v.recordings} rec · {v.items} items · {v.minutes}m
                </span>
              </div>
              <div className="h-1 bg-white/[0.05] rounded-[1px] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-rose-500"
                  style={{ width: `${(v.recordings / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="glass-strong p-6">
    <div className="label-mono mb-3">{label}</div>
    <div className="font-display text-5xl tightest font-semibold tabular leading-none">{value}</div>
  </div>
);

export default Analytics;
